import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  DataTable,
  EmptyState,
  MarginIndicator,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
  VendorCostBreakdown,
  ViewToggle,
} from '@/components/ui'
import { classifyMargin } from '@/lib/utils/margin-thresholds'
import { useMargins } from '../api/queries'
import type { GroupBy, MarginRow } from '../api/types'
import { MarginSummaryChart } from '../components/MarginSummaryChart'
import { MarginsFilterRail } from '../components/MarginsFilterRail'
import {
  isPresetValue,
  rangeForPreset,
  type DateRange,
  type PresetValue,
} from '../components/date-presets'

type ViewMode = 'table' | 'summary'

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'product', label: 'Product' },
  { value: 'customer', label: 'Customer' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'salesperson', label: 'Salesperson' },
  { value: 'month', label: 'Month' },
]

const DEFAULT_PRESET: PresetValue = 'ytd'
const PAGE_SIZE = 50

export function MarginsPage() {
  const navigate = useNavigate()
  // Read `?range=...` (and `?from=&to=` for custom) to seed the active
  // preset; clamp to a known value so an invalid URL param doesn't
  // crash the page.
  const search = useSearch({ strict: false }) as {
    range?: string
    from?: string
    to?: string
  }
  const initialPreset: PresetValue | null =
    search?.range && isPresetValue(search.range)
      ? search.range
      : search?.range === 'custom'
        ? null
        : DEFAULT_PRESET
  const initialCustomRange: DateRange =
    search?.range === 'custom' && search.from && search.to
      ? { from: search.from, to: search.to }
      : rangeForPreset(DEFAULT_PRESET)

  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [groupBy, setGroupBy] = useState<GroupBy>('product')
  const [searchInput, setSearchInput] = useState('')
  const [debounced, setDebounced] = useState('')
  const [preset, setPreset] = useState<PresetValue | null>(initialPreset)
  const [customRange, setCustomRange] = useState<DateRange>(initialCustomRange)
  const dates = useMemo(
    () => (preset ? rangeForPreset(preset) : customRange),
    [preset, customRange],
  )
  const [filterRailCollapsed, setFilterRailCollapsed] = useState(false)
  const [page, setPage] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)

  const handlePresetChange = (next: PresetValue | null) => {
    if (next) {
      setPreset(next)
    } else {
      // Re-clicking the active chip drops into custom mode, seeded with
      // whatever range was already showing so the visible dates don't jump.
      setCustomRange(dates)
      setPreset(null)
    }
  }

  const handleCustomRangeChange = (next: DateRange) => {
    setCustomRange(next)
    setPreset(null)
  }

  // Reflect preset (or custom from/to) to the URL (replace, not push,
  // so the back-button doesn't fill with date-preset toggles).
  useEffect(() => {
    navigate({
      to: '.',
      search: (prev: Record<string, unknown> | undefined) => {
        const base = { ...(prev ?? {}) }
        delete base.from
        delete base.to
        if (preset) {
          return { ...base, range: preset }
        }
        return { ...base, range: 'custom', from: customRange.from, to: customRange.to }
      },
      replace: true,
    })
  }, [preset, customRange, navigate])

  useEffect(() => {
    const id = setTimeout(() => setDebounced(searchInput), 200)
    return () => clearTimeout(id)
  }, [searchInput])

  // Any of these invalidate the current page — jump back to page 1
  // rather than risk an out-of-range offset against the new result set.
  // Adjusted during render (not in an effect) per the React-recommended
  // pattern for resetting state when inputs change — avoids an extra
  // cascading render (react-hooks/set-state-in-effect).
  const pageResetKey = `${groupBy}|${dates.from}|${dates.to}|${debounced}`
  const [prevPageResetKey, setPrevPageResetKey] = useState(pageResetKey)
  if (pageResetKey !== prevPageResetKey) {
    setPrevPageResetKey(pageResetKey)
    setPage(0)
  }

  const { data, isLoading, isFetching, isError, error, refetch } = useMargins({
    group_by: groupBy,
    date_from: dates.from,
    date_to: dates.to,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debounced,
  })

  const rows = useMemo<MarginRow[]>(() => {
    const first = data?.breakdown?.[0]
    return first?.data ?? []
  }, [data])

  const totalGroups = data?.breakdown?.[0]?.total_groups ?? rows.length
  const totalPages = Math.max(1, Math.ceil(totalGroups / PAGE_SIZE))

  const filtered = useMemo<MarginRow[]>(() => {
    const needle = debounced.trim().toLowerCase()
    if (!needle) return rows
    // Null-guard on group_key — the backend occasionally returns rows
    // with a null key (e.g. salesperson groupings for SOs without an
    // assigned user). Crashed search in prod 2026-04-24.
    return rows.filter(
      (r) => r.group_key?.toLowerCase().includes(needle) ?? false,
    )
  }, [rows, debounced])

  const summary = data?.summary

  const columns = useMemo<ColumnDef<MarginRow, unknown>[]>(
    () => {
      // Sprint 09 Item 7: backend now always-populates `ordered_*`
      // keys with 0.0 defaults regardless of group_by. The frontend
      // continues to only SHOW the columns in product view since
      // booked $ / booked % aren't meaningful for other dimensions
      // (sales orders aren't grouped by customer/vendor/etc. with
      // matching keys to invoiced rows).
      const showOrdered = groupBy === 'product'

      const cols: ColumnDef<MarginRow, unknown>[] = [
        {
          id: 'group_key',
          header: GROUP_OPTIONS.find((o) => o.value === groupBy)?.label ?? 'Group',
          accessorKey: 'group_key',
          cell: (ctx) => (ctx.getValue() as string | null) ?? '—',
        },
      ]

      if (showOrdered) {
        cols.push(
          {
            id: 'ordered_revenue',
            header: 'Booked $',
            accessorKey: 'ordered_revenue',
            cell: (ctx) => currencyOrDash(ctx.getValue()),
          },
          {
            id: 'ordered_margin_pct',
            header: 'Booked %',
            accessorKey: 'ordered_margin_pct',
            cell: (ctx) => {
              const v = ctx.getValue() as number | null | undefined
              if (v == null) return <span className="text-tertiary">—</span>
              return <MarginIndicator value={v} showBar={false} />
            },
          },
        )
      }

      cols.push(
        {
          id: 'invoiced_revenue',
          header: 'Invoiced $',
          accessorKey: 'invoiced_revenue',
          cell: (ctx) => currencyOrDash(ctx.getValue()),
        },
        {
          id: 'actual_cogs',
          header: 'COGS',
          accessorKey: 'actual_cogs',
          cell: (ctx) => currencyOrDash(ctx.getValue()),
        },
        {
          id: 'true_margin',
          header: 'True $',
          accessorKey: 'true_margin',
          cell: (ctx) => currencyOrDash(ctx.getValue()),
        },
        {
          id: 'true_margin_pct',
          header: 'True %',
          accessorKey: 'true_margin_pct',
          cell: (ctx) => {
            const v = ctx.getValue() as number | null | undefined
            if (v == null) return <span className="text-tertiary">—</span>
            return <MarginIndicator value={v} showBar={false} />
          },
        },
        {
          id: 'qty_invoiced',
          header: 'Qty',
          accessorKey: 'qty_invoiced',
          cell: (ctx) => {
            const v = ctx.getValue() as number | null | undefined
            if (v == null) return <span className="text-tertiary">—</span>
            return v.toLocaleString()
          },
        },
        {
          id: 'vendor_cost_breakdown',
          header: 'Vendor cost',
          accessorKey: 'vendor_cost_breakdown',
          cell: (ctx) => (
            <VendorCostBreakdown
              breakdown={ctx.row.original.vendor_cost_breakdown}
              blendedUnitCost={ctx.row.original.vendor_cost_blended_per_unit}
            />
          ),
        },
        {
          id: 'quality',
          header: 'Quality',
          accessorKey: 'quality',
          cell: (ctx) => {
            const q = ctx.getValue() as string | null | undefined
            if (q == null) return <span className="text-tertiary">—</span>
            const tone =
              q === 'high'
                ? 'text-success-text'
                : q === 'medium'
                  ? 'text-warning-text'
                  : 'text-danger-text'
            return <span className={`text-2xs uppercase ${tone}`}>{q}</span>
          },
        },
      )

      return cols
    },
    [groupBy],
  )

  // `/` focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div data-density="spacious" className="flex h-full flex-col">
      <PageHeaderBand
        title="Margins"
        description="True margin — invoiced revenue minus actual COGS from valuation layers — alongside booked margin for the same period."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard
            variant="inline"
            label="Booked rev"
            value={currency(summary?.ordered_revenue ?? 0)}
          />
          <MetricCard
            variant="inline"
            label="Invoiced rev"
            value={currency(summary?.invoiced_revenue ?? 0)}
          />
          <MetricCard
            variant="inline"
            label="COGS"
            value={currency(summary?.actual_cogs ?? 0)}
          />
          <MetricCard
            variant="inline"
            label="True margin"
            value={currency(summary?.true_margin ?? 0)}
            valueTone={
              classifyMargin(summary?.true_margin_pct ?? 0) === 'success'
                ? 'default'
                : 'warning'
            }
          />
          <MetricCard
            variant="inline"
            label="True margin %"
            value={`${(summary?.true_margin_pct ?? 0).toFixed(1)}%`}
            valueTone={
              classifyMargin(summary?.true_margin_pct ?? 0) === 'success'
                ? 'default'
                : 'warning'
            }
          />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1">
        <MarginsFilterRail
          preset={preset}
          onPresetChange={handlePresetChange}
          dates={dates}
          onCustomRangeChange={handleCustomRangeChange}
          collapsed={filterRailCollapsed}
          onToggleCollapsed={() => setFilterRailCollapsed((v) => !v)}
        />

        <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder={`Search ${GROUP_OPTIONS.find((o) => o.value === groupBy)?.label.toLowerCase() ?? 'group'}…`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <ViewToggle
              aria-label="View mode"
              value={viewMode}
              onChange={(next) => {
                setViewMode(next)
                // The Summary chart's top-10-by-revenue is only correct
                // when fed page 1's globally-sorted data.
                if (next === 'summary') setPage(0)
              }}
              options={[
                { value: 'table', label: 'Table' },
                { value: 'summary', label: 'Summary' },
              ]}
            />
            <ToolbarDivider />
            <div className="flex flex-wrap gap-1.5">
              {GROUP_OPTIONS.map((o) => {
                const active = groupBy === o.value
                return (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setGroupBy(o.value)}
                    className="inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2.5 py-1 text-xs text-primary hover:bg-surface-hover aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text"
                  >
                    {o.label}
                  </button>
                )
              })}
            </div>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>
              {totalGroups === 0
                ? '0 of 0'
                : `${(page * PAGE_SIZE + 1).toLocaleString()}–${Math.min(
                    totalGroups,
                    page * PAGE_SIZE + rows.length,
                  ).toLocaleString()} of ${totalGroups.toLocaleString()}`}
            </ToolbarCount>
            {viewMode === 'table' ? (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Previous page"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <ChevronLeft size={14} />
                </Button>
                <span className="whitespace-nowrap text-xs text-secondary tabular-nums">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Next page"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight size={14} />
                </Button>
              </div>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
          {viewMode === 'table' ? (
            <DataTable
              error={isError ? error : undefined}
              onRetry={() => void refetch()}
              errorSubject="margins"
              data={filtered}
              columns={columns}
              loading={isLoading}
              // 'danger' (not the broader danger+warning band) is the closer
              // analog to the old flat 20% cutoff — DataTable's "critical"
              // accent is specifically the red/danger color, not amber.
              criticalRowPredicate={(r) =>
                r.true_margin_pct != null && classifyMargin(r.true_margin_pct) === 'danger'
              }
              getRowId={(r) => r.group_key ?? `__null_${r.row_count}_${r.invoiced_revenue ?? 0}`}
              moduleId="margins"
              maxHeight="100%"
              columnWidths={{
                ordered_revenue: 130,
                ordered_margin_pct: 110,
                invoiced_revenue: 130,
                actual_cogs: 130,
                true_margin: 130,
                true_margin_pct: 110,
                qty_invoiced: 110,
                vendor_cost_breakdown: 130,
                quality: 90,
              }}
              minTableWidth={1300}
              emptyState={
                <EmptyState
                  variant={rows.length === 0 ? 'no-data' : 'no-match'}
                  title={rows.length === 0 ? 'No data for this period' : 'No matches'}
                  description="Try changing the group-by or broadening the search."
                />
              }
            />
          ) : (
            <MarginSummaryChart rows={filtered} topN={10} />
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-border-subtle bg-surface-sunk px-3 py-1.5 text-2xs text-secondary">
          <span>
            {dates.from} → {dates.to} · grouped by{' '}
            {GROUP_OPTIONS.find((o) => o.value === groupBy)?.label}
          </span>
          <span>/ focus search</span>
        </footer>
        </div>
      </div>
    </div>
  )
}

function currency(n: number): string {
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

/** Null-safe currency cell renderer for table cells where the backend
 *  field is conditionally populated (e.g. `ordered_*` only set when
 *  group_by === 'product'). Returns "—" styled tertiary-text for null. */
function currencyOrDash(v: unknown): React.ReactNode {
  if (v == null) return <span className="text-tertiary">—</span>
  if (typeof v !== 'number') return String(v)
  return currency(v)
}
